import React, { useState } from 'react';
import usePortalAuth from '../../../auth/usePortalAuth';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import UpdatedCircleWidget from '../CircleWidget/UpdatedCircleWidget';
import companyLogo from '../../LandingPages/Images/logo-here.png';
import logoBadge from '../Assets/Logos/WhiteShield.png';
import { DateCreateFullDate } from '../DateCreate/DateCreateFullDate';

//--------------Material UI Imports--------------//
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import MSSPClientCardDialogue from './MSSPClientCardDialogue.js';
import MSSPSparkLineChart from './MSSPSparkLineChart';
import './MSSP.css';

const MSSPClientCard = (props) => {
  const [MSSPClientCardDialogueState, setMSSPClientCardDialogueState] = useState(false);
  const toggleMSSPClientCardDialogue = () => setMSSPClientCardDialogueState(!MSSPClientCardDialogueState);
  const [expanded, setExpanded] = useState(false);

  const preferredLanguage = props.preferredLanguage;
  const getTranslation = props.getTranslation;

  const handleExpandClick = () => setExpanded(!expanded);

  const useStyles = makeStyles({
    gridItem: {
      maxHeight: 700,
      minWidth: 420,
    },
    cardPaper: {
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    },
    cardBody: {
      padding: '16px 20px 20px',
    },
    companyName: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontSize: '1.05rem',
      lineHeight: 1.3,
    },
    metaText: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '0.75rem',
      opacity: 0.65,
      marginTop: 2,
    },
    sectionLabel: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      opacity: 0.5,
      marginBottom: 6,
    },
    metricRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
    },
    metricLabel: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '0.82rem',
      flex: 1,
    },
    metricSparkline: {
      width: 120,
      minWidth: 100,
      marginLeft: 12,
    },
    threatChipsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 4,
    },
    actionRow: {
      display: 'flex',
      gap: 10,
      marginTop: 16,
    },
  });

  const classes = useStyles();

  const getAccentColor = () => {
    if (!props.riskLevel) return '#888888';
    if (props.riskLevel >= 86) return '#e53935';
    if (props.riskLevel >= 61) return '#f1c411';
    return '#2CCC72';
  };

  const statusDotColor = (score) => {
    if (score === 'green') return '#2CCC72';
    if (score === 'yellow') return '#f1c411';
    if (score === 'red') return '#e53935';
    return '#aaaaaa';
  };

  const StatusDot = ({ score }) => {
    const color = statusDotColor(score);
    return (
      <Tooltip title={score || 'N/A'}>
        <span
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
            boxShadow: `0 0 6px ${color}99`,
          }}
        />
      </Tooltip>
    );
  };

  const ThreatChip = ({ label, score }) => {
    const color = statusDotColor(score);
    const isYellow = score === 'yellow';
    return (
      <Tooltip title={label}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            borderRadius: 20,
            backgroundColor: `${color}1a`,
            border: `1px solid ${color}55`,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'Montserrat, sans-serif',
            color: isYellow ? '#9a7800' : color,
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isYellow ? '#c49a00' : color,
              flexShrink: 0,
            }}
          />
          {label}
        </span>
      </Tooltip>
    );
  };

  var userRiskBorder = 'defaultClientCardBorder';
  if (props.riskLevel) {
    if (props.riskLevel >= 86) {
      var userRiskBorder = 'highClientCardBorder';
    } else if (props.riskLevel <= 60) {
      var userRiskBorder = 'lowClientCardBorder';
    } else if (props.riskLevel >= 61 && props.riskLevel <= 85) {
      var userRiskBorder = 'medClientCardBorder';
    }
  }

  const setClientAndPushToCompanyOverview = () => {
    props.setSelectedClient(props.clientId);
    if (props.history?.push) {
      props.history.push('/Dashboard/CompanyOverview');
    }
  };

  const CardContents = (cardProps) => {
    return (
      <div>
        <div className="mssp-accent-bar" ref={el => { if (el) el.style.setProperty('background-color', getAccentColor(), 'important'); }} />
        <div className={classes.cardBody}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
            <img
              id="companyLogoMSSPCard"
              src={cardProps.companyLogo ? cardProps.companyLogo : companyLogo}
              alt="Company Logo"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.companyName}>{cardProps.companyName}</Typography>
              <Typography className={classes.metaText}>
                {getTranslation('Contact', preferredLanguage)}: {cardProps.companyContact}
              </Typography>
              <Typography className={classes.metaText}>
                {getTranslation('Last Contacted', preferredLanguage)}: <DateCreateFullDate date={cardProps.lastContactedDate} />
              </Typography>
            </div>
            {!MSSPClientCardDialogueState ? (
              <Tooltip title={getTranslation('View Large Card', preferredLanguage)}>
                <IconButton size="small" aria-label="View Large Card" onClick={toggleMSSPClientCardDialogue}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton size="small" aria-label="Close Card" onClick={toggleMSSPClientCardDialogue}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          <Divider style={{ marginBottom: 10 }} />

          <Typography className={classes.sectionLabel}>
            {getTranslation('Metrics', preferredLanguage)}
          </Typography>
          <div className={classes.metricRow}>
            <Typography className={classes.metricLabel}>
              {getTranslation('Active Alerts', preferredLanguage)}
            </Typography>
            <StatusDot score={cardProps.alertScore} />
            <div className={classes.metricSparkline}>
              <MSSPSparkLineChart
                color="rgb(238, 49, 30)"
                color2="rgb(241, 196, 17)"
                data={cardProps.chartDataAlerts}
              />
            </div>
          </div>
          <Divider light />
          <div className={classes.metricRow}>
            <Typography className={classes.metricLabel}>
              {getTranslation('Active Users', preferredLanguage)}
            </Typography>
            <StatusDot score={cardProps.activeUserScore} />
            <div className={classes.metricSparkline}>
              <MSSPSparkLineChart color="#2CCC72" data={cardProps.chartDataActiveUsers} />
            </div>
          </div>

          <Divider style={{ margin: '10px 0 8px' }} />

          {props.cardsPerRow === 4 && !MSSPClientCardDialogueState ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography className={classes.sectionLabel}>
                  {getTranslation('Threat Model', preferredLanguage)}
                </Typography>
                <Tooltip title={!expanded ? getTranslation('View Threat Model Status', preferredLanguage) : getTranslation('Close Threat Model Status', preferredLanguage)}>
                  <IconButton size="small" onClick={handleExpandClick} aria-expanded={expanded} aria-label="expand threat model">
                    {!expanded ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </div>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={classes.threatChipsRow}>
                  <ThreatChip label={getTranslation('Browser Email', preferredLanguage)} score={props.threatModel.browserEmail} />
                  <ThreatChip label={getTranslation('Chat Apps', preferredLanguage)} score={props.threatModel.chatApps} />
                  <ThreatChip label={getTranslation('Email Apps', preferredLanguage)} score={props.threatModel.emailApp} />
                  <ThreatChip label={getTranslation('Search Engines', preferredLanguage)} score={props.threatModel.searchEngine} />
                  <ThreatChip label={getTranslation('Social Media', preferredLanguage)} score={props.threatModel.socialMedia} />
                </div>
              </Collapse>
            </>
          ) : (
            <>
              <Typography className={classes.sectionLabel}>
                {getTranslation('Threat Model', preferredLanguage)}
              </Typography>
              <div className={classes.threatChipsRow}>
                <ThreatChip label={getTranslation('Browser Email', preferredLanguage)} score={props.threatModel.browserEmail} />
                <ThreatChip label={getTranslation('Chat Apps', preferredLanguage)} score={props.threatModel.chatApps} />
                <ThreatChip label={getTranslation('Email Apps', preferredLanguage)} score={props.threatModel.emailApp} />
                <ThreatChip label={getTranslation('Search Engines', preferredLanguage)} score={props.threatModel.searchEngine} />
                <ThreatChip label={getTranslation('Social Media', preferredLanguage)} score={props.threatModel.socialMedia} />
              </div>
            </>
          )}

          <div className={classes.actionRow}>
            <Button
              variant="outlined"
              onClick={cardProps.toggle}
              style={{ fontSize: '12px', flex: 1, borderColor: '#3f51b5', color: '#3f51b5' }}
            >
              {getTranslation('Send / Generate Report', preferredLanguage)}
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={setClientAndPushToCompanyOverview}
              style={{ fontSize: '12px', flex: 1, backgroundColor: '#3f51b5', color: '#fff' }}
            >
              {getTranslation('Access Account', preferredLanguage)}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const ListContent = () => {
    return (
      <List>
        <Paper id={userRiskBorder}>
          <ListItem>
            <ListItemAvatar>
              <Avatar variant="square" src={props.companyLogo} />
            </ListItemAvatar>
            <ListItemText primary={props.companyName} />
            <ListItemSecondaryAction>
              <Tooltip title={getTranslation('View Large Card', preferredLanguage)}>
                <IconButton aria-label="View Large Card" edge="end" onClick={toggleMSSPClientCardDialogue}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        </Paper>
      </List>
    );
  };

  return (
    <React.Fragment>
      {!props.listView ? (
        <Grid item className={classes.gridItem} xs={12} sm={12} md={8} lg={props.cardsPerRow}>
          <Paper className={classes.cardPaper}>
            {CardContents(props)}
          </Paper>
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {ListContent()}
        </Grid>
      )}
      <MSSPClientCardDialogue
        toggle={toggleMSSPClientCardDialogue}
        isOpen={MSSPClientCardDialogueState}
        userRiskBorder={userRiskBorder}
        content={CardContents(props)}
        preferredLanguage={preferredLanguage}
        getTranslation={getTranslation}
      />
    </React.Fragment>
  );
};
export default MSSPClientCard;
